import React, { useState, useEffect, useRef, Fragment } from "react";
import ApiClient from "../../../../methods/api/apiClient";
import loader from "../../../../methods/loader";
import moment from 'moment';
import swal from "sweetalert";
import { Dialog, Transition } from '@headlessui/react'; // Import Headless UI

const Index = (props) => {
  const [dragging, setDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [responselist, setresponselist] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef(null);

  // New States for Product Modal
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  const fetchProducts = async () => {
    loader(true);
    await ApiClient.get("product/listing")
      .then((res) => { 
        if (res?.success) {
          // Normalize data structure based on your snippet
          setProducts(res?.data?.products || res?.data || []); 
        }
      })
      .catch(console.error);
    loader(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  // Triggered when files are dropped/selected
  const initiateUpload = (files) => {
    if (!files.length) return;
    setPendingFiles(files);
    fetchProducts();
    setIsProductModalOpen(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    initiateUpload(files);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    initiateUpload(files);
  };

  const [Files, setFiles] = useState([]);

  // Final upload called after product selection
  const handleFiles = (files, productId) => {
    loader(true);
    // Added productId to the payload
    ApiClient.postFormData('upload/pdfs', { pdfs: files, productId: productId })
      .then(res => {
        if (res.fileName) {
          const newFiles = files.map(file => ({ name: file.name, path: res.fileName }));
          setFiles(prev => [...prev, ...newFiles]);
        }
        swal({ text: 'File uploaded successfully.', icon: 'success', className: 'smallModal' });
        fetchData();
        loader(false);
        setIsProductModalOpen(false);
        setPendingFiles([]);
      })
      .catch(err => {
        console.error(err);
        loader(false);
      });
  };

  const fetchData = async () => {
    await ApiClient.get(`sessions/listing?urlSession=false`).then(async res => {
      if (res?.success) {
        setresponselist(res.data);
      }
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const DeleteOneData = async (id) => {
    const confirm = await swal({ text: `Delete selected file?`, icon: "warning", buttons: true, dangerMode: true });
    if (!confirm) return;
    loader(true);
    await ApiClient.delete(`sessions/delete?id=${id}`)
      .then(async res => {
        await fetchData();
        swal({ text: 'File deleted successfully.', icon: 'success', className: 'smallModal' });
      })
      .catch(error => { });
    loader(false);
  };

  const handleViewPdf = (filePath) => {
    window.open(filePath, '_blank');
    setOpenDropdownId(null);
  };

  const toggleDropdown = (id) => {
    setOpenDropdownId(prev => (prev === id ? null : id));
  };

  return (
    <>
      {/* Product Selection Modal */}
      <Transition appear show={isProductModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={() => setIsProductModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 border-b pb-3">
                    Select Product
                  </Dialog.Title>
                  <div className="mt-4 max-h-[300px] overflow-y-auto space-y-2">
                    {products.length > 0 ? (
                      products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => handleFiles(pendingFiles, (product?.id||product?._id))}
                          className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-[#115B9D] hover:bg-[#115B9D0A] transition-all flex justify-between items-center group"
                        >
                          <span className="text-gray-700 group-hover:text-[#115B9D] font-medium">{product?.productName || product.title}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600 group-hover:text-[#115B9D]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No products found.</p>
                    )}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none"
                      onClick={() => setIsProductModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <div className="i w-full border border-t-0 rounded-b-2xl bg-white p-5 lg:p-8">
        <div className="">
          <div className="space-y-4">
            <h3 className="text-md sm:text-lg text-gray-900">Files</h3>
            <div className="">
              <label className="">
                <div
                  className={`relative border-2 border-dashed rounded-2xl p-8 sm:p-12 text-center cursor-pointer transition-all border-[#115B9D] ${dragging ? "border-[#115B9D] bg-[#115B9D]/10" : "border-dashed"
                    } rounded-xl cursor-pointer bg-white hover:bg-[#115B9D17]`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className="size-16 rounded-full bg-[#115b9d29] flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-upload size-8 text-[#115B9D]">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" x2="12" y1="3" y2="15"></line>
                      </svg>
                    </div>
                    <div className="">
                      <p className="text-sm sm:text-md text-[#115B9D] mb-1">Drag or drop files here, or click to select files</p>
                      <p className="text-xs sm:text-sm text-gray-500">Support file types: .pdf</p>
                    </div>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    multiple
                    accept=".pdf"
                  />
                </div>
              </label>
            </div>
            <p className="text-sm text-gray-500 text-center">
              If you are uploading a PDF, make sure you can select/highlight the text.
            </p>
          </div>
        </div>

        {/* Existing Table Code Remains Exactly the Same */}
        <div className="space-y-4 mt-8">
          <h3 className="text-md sm:text-lg text-gray-900">Already Included Files</h3>
          {/* Mobile View */}
<div className="md:!hidden space-y-4">
  {responselist.map((item, index) => (
    <div
      key={index}
      className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3 min-w-0">
          <div className="size-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-600"
            >
              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
              <path d="M14 2v4a2 2 0 0 0 2 2h4" />
            </svg>
          </div>

          <div className="min-w-0">
            <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
              {item?.files?.map(f => f?.filename).join(", ")}
            </h3>

            <p className="text-xs text-gray-500 mt-1">
              {moment(item?.createdAt).format("YYYY-MM-DD HH:mm")}
            </p>
          </div>
        </div>

       {item?.status === "active" ? (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-xs font-medium">
    <div className="size-1.5 rounded-full bg-[#008236]" />
    Success
  </span>
) : (
  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-xs font-medium">
    <div className="size-2 rounded-full bg-[#115B9D] animate-pulse" />
    Pending
  </span>
)}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Characters</p>
          <p className="text-sm font-semibold text-gray-900">
            {item?.files?.[0]?.characters || 0}
          </p>
        </div>

        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-500">Tokens</p>
          <p className="text-sm font-semibold text-gray-900">
            {item?.files?.[0]?.chunkCount || 0}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        {item?.files?.length > 0 && (
          <button
            onClick={() =>
              handleViewPdf(
                item?.files?.[0]?.downloadUrl ||
                  item?.files?.[0]?.url ||
                  item?.files?.[0]?.path
              )
            }
            className="flex-1 h-10 rounded-xl bg-[#115B9D] text-white text-sm font-medium"
          >
            View PDF
          </button>
        )}

        <button
          onClick={() => DeleteOneData(item?.id)}
          className="h-10 px-4 rounded-xl border border-red-200 text-red-600 text-sm font-medium"
        >
          Delete
        </button>
      </div>
    </div>
  ))}
</div>

{/* Desktop Table */}
<div className="max-md:!hidden overflow-auto scrollbar border border-gray-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-5" scope="col"><span className="text-sm text-gray-700 font-[400] whitespace-nowrap">File Names</span></th>
                  <th className="text-left py-3 px-5" scope="col"><span className="text-sm text-gray-700 font-[400] whitespace-nowrap">Upload Date</span></th>
                  <th className="text-left py-3 px-5" scope="col"><span className="text-sm text-gray-700 font-[400] whitespace-nowrap">File Size</span></th>
                  <th className="text-left py-3 px-5" scope="col"><span className="text-sm text-gray-700 font-[400] whitespace-nowrap">Status</span></th>
                  <th className="text-left py-3 px-5" scope="col"><span className="text-sm text-gray-700 font-[400] whitespace-nowrap">Action</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {responselist.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50/60 transition-colors">
                    <td className="py-3 px-5">
                        <p className="min-w-[200px] max-w-[280px] text-sm font-medium text-gray-900 flex items-center gap-2 whitespace-nowrap">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text min-w-[20px] w-[20px] text-gray-600">
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                         <span className="break-all whitespace-normal line-clamp-2"> {item?.files?.map((_itm) => _itm?.filename)?.join(', ')}</span>
                        </p>
                    </td>
                    <td className="py-3 px-5">
                      <p className="text-sm text-gray-900 flex items-center gap-2 whitespace-nowrap">
                        {moment(item?.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      </p>
                    </td>
                    <td className="py-3 px-5">
                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {item?.files[0]?.characters ? item?.files[0]?.characters : 0} Char / {item?.files[0]?.chunkCount ? item?.files[0]?.chunkCount : 0} Tokens
                      </p>
                    </td>
                    <td className="py-3 px-5">
                      {item?.status === "active" ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#f0fdf4] text-[#008236] text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-circle-check size-4"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></svg>
                          Success
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1F54FF1A] text-[#2B2B2B] text-sm">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-loader-2 size-4"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-3">
                        {item?.files?.length > 0 && (
                          <div className="relative" ref={openDropdownId === item?.id ? dropdownRef : null}>
                            <button onClick={() => toggleDropdown(item?.id)} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D]">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                            </button>
                            {openDropdownId === item?.id && (
                              <div className="absolute z-50 right-0 mt-1 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                <p className="text-xs text-gray-500 px-3 py-2 border-b">Select PDF to View</p>
                                {item?.files?.map((_itm, i) => (
                                  <button key={i} onClick={() => handleViewPdf(_itm?.downloadUrl || _itm?.url || _itm?.path)} className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-[#115B9D17] hover:text-[#115B9D] text-sm text-gray-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-gray-600"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path></svg>
                                    <span className="truncate">{_itm?.filename || `File ${i + 1}`}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        <button onClick={() => DeleteOneData(item?.id)} className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </>
  );
};

export default Index;